import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { Button } from 'components/ui/button';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';
import type { FigureCell, FigureRangeCell, FigureRecordCell, FigureRows, FigureRowsColumn } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { FigureCellValue } from './FigureCellValue';
import { FigureCopyButton } from './FigureCopyButton';
import { columnLabel } from './figureMeta';
import { cellText, columnDecimals, isRangeCell, rangeEnd, recordOf } from './figureRows';

// Cells at the box's edges keep clear of its border.
const CELL_CLASS_NAME = 'px-2 py-1.5 first:ps-3 last:pe-3';

const NUMERIC: FigureRowsColumn['type'][] = ['number', 'hours', 'money', 'percentage'];

// A number or a date, and its header, keep one line: a squeezed table scrolls in its box rather than break them.
const ONE_LINE: FigureRowsColumn['type'][] = [...NUMERIC, 'date'];

// A record chip shows whole, as the boards draw it, and its column is as wide, so a table still wider than its box once
// the yielding column below gave way scrolls in it. Only a name past 16rem truncates, its number kept; the ring sits
// inside, where the truncating wrapper keeps it.
const TABLE_CHIP_CLASS_NAME = cn('max-w-64', FOCUS_RING_INSET);

// A chip cut at its cell's width, in a cell that takes no width of its own: an auto-layout table grows to fit a cell
// capped by a percentage alone. A run's heading does it with `max-w-0` on its cell, the yielding column below with
// its wrapper.
const CUT_CHIP_CLASS_NAME = cn('max-w-full', FOCUS_RING_INSET);

// From 32rem the second record's column yields, as the first record's stacking does below it: its chips whole up to
// 16rem when the table fits, cut to what the other columns leave when it would not, so the values stay in view. A
// one-track grid with a 0 minimum asks the table for no width; a table still too wide scrolls in its box.
const YIELDING_CLASS_NAME = 'grid max-w-64 grid-cols-[minmax(0,auto)]';

/** The outlined box a Figure's table sits in, inside the Figure, so its start and end are clear. It scrolls when wider. */
export const TABLE_BOX_CLASS_NAME = 'border-border mx-4 mb-1.5 overflow-x-auto rounded-[10px] border wrap-normal';

// A cell of several records shows them all up to three; past three, the first two and "+N", which shows the rest.
const CHIP_CAP = 3;
const CHIPS_BEFORE_MORE = 2;

// Each chip and "+N" keep `hit-area`'s target, 7px past a chip each way (13px under a coarse pointer): the list keeps
// that room around and between its lines, so no target reaches another's, in the cell or the next row.
const RECORDS_CLASS_NAME = cn(
    'flex flex-wrap items-start gap-x-1 gap-y-3.5 py-1.75',
    'pointer-coarse:gap-x-3 pointer-coarse:gap-y-6.5 pointer-coarse:py-3.25',
    FOCUS_RING_INSET
);

interface CellProps {
    cell: FigureCell;
    column: FigureRowsColumn;
    /** Every record of a cell of several, as at full size. */
    allRecords?: boolean;
    chipClassName?: string;
    /** A number or percentage column's decimals. */
    decimals?: number;
}

/**
 * Several records, a chip for each in the list's order, wrapping inside a table cell or a record card's field. A chip
 * keeps its own title for a cut name: `EllipsisTooltip`'s wrapper would clip its target to its line.
 */
export function RecordChips({
    cells,
    column,
    allRecords,
    chipClassName = TABLE_CHIP_CLASS_NAME,
    className,
}: Omit<CellProps, 'cell'> & { cells: FigureRecordCell[]; className?: string }) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const listRef = useRef<HTMLSpanElement>(null);
    const shown = allRecords || open || cells.length <= CHIP_CAP ? cells.length : CHIPS_BEFORE_MORE;

    const reveal = () => {
        const linksBefore = listRef.current?.querySelectorAll('a').length ?? 0;
        flushSync(() => setOpen(true));
        // The cell itself when what "+N" revealed has no link: "+N" is gone, and focus stays in the cell.
        (listRef.current?.querySelectorAll('a')[linksBefore] ?? listRef.current)?.focus();
    };

    if (!cells.length) {
        return null;
    }
    return (
        <span ref={listRef} tabIndex={-1} className={cn(RECORDS_CLASS_NAME, className)}>
            {cells.slice(0, shown).map((cell) =>
                recordOf(cell) ? (
                    <FigureCellValue
                        key={`${cell.entityType}:${cell.id}`}
                        cell={cell}
                        column={column}
                        chipClassName={chipClassName}
                    />
                ) : (
                    // A record this build cannot open is its name, in a box of its own so the gap stays between two.
                    <span key={`${cell.entityType}:${cell.id}`}>{cellText(cell)}</span>
                )
            )}
            {shown < cells.length ? (
                <button
                    type="button"
                    onClick={reveal}
                    aria-label={intl.formatMessage({ id: 'plus-n-more' }, { count: cells.length - shown })}
                    className={cn(
                        NEUTRAL_TILE_CLASS,
                        'hit-area relative cursor-pointer rounded-md px-1.5 font-medium tabular-nums hover:underline',
                        FOCUS_RING_INSET
                    )}
                >
                    +{cells.length - shown}
                </button>
            ) : null}
        </span>
    );
}

/** A cell's value; a record's chip held to the table's cap, its whole label on hover and focus once cut. */
function CellValue({ cell, column, allRecords, chipClassName = TABLE_CHIP_CLASS_NAME, decimals }: CellProps) {
    if (Array.isArray(cell)) {
        return <RecordChips cells={cell} column={column} allRecords={allRecords} chipClassName={chipClassName} />;
    }
    return recordOf(cell) ? (
        <EllipsisTooltip>
            <FigureCellValue cell={cell} column={column} chipClassName={chipClassName} hideChipTitle />
        </EllipsisTooltip>
    ) : (
        <FigureCellValue cell={cell} column={column} decimals={decimals} />
    );
}

/**
 * A footer's range: the column's lowest and highest value in its format, one over the other, each with the row that
 * holds it under it, so the column grows no wider than its values. Equal ends come as a plain value, never a range.
 */
function RangeValue({
    cell,
    column,
    decimals,
}: {
    cell: FigureRangeCell;
    column: FigureRowsColumn;
    decimals?: number;
}) {
    const intl = useIntl();
    const end = (value: number, at: string | undefined) => (
        <span className="flex flex-col items-end">
            <FigureCellValue cell={rangeEnd(cell, column, value)} column={column} decimals={decimals} />
            {at ? (
                // A long row name is cut at 10rem rather than widen the column, whole on hover.
                <span className="text-caption text-foreground-tertiary max-w-40 normal-nums">
                    <EllipsisTooltip>{at}</EllipsisTooltip>
                </span>
            ) : null}
        </span>
    );
    return (
        <span className="flex flex-col items-end gap-1.5">
            {end(cell.min, cell.minAt)}
            <span className="sr-only">{intl.formatMessage({ id: 'ai-chat.figure.range-to' })}</span>
            {end(cell.max, cell.maxAt)}
        </span>
    );
}

/** "Show {count} more" and "Show fewer", under a Figure's rows. */
export function ShowMoreButton({ open, more, onToggle }: { open: boolean; more: number; onToggle: () => void }) {
    const intl = useIntl();
    return (
        <Button
            variant="outline"
            aria-expanded={open}
            onClick={onToggle}
            className="text-meta border-border-control bg-card text-foreground hover:bg-muted h-8 rounded-lg px-3 shadow-none"
        >
            {intl.formatMessage(
                { id: open ? 'ai-chat.figure.show-fewer' : 'ai-chat.figure.show-more' },
                { count: more }
            )}
        </Button>
    );
}

interface Props {
    rows: FigureRows;
    /** The rows shown until "Show more"; every row when left out, as at full size. */
    limit?: number;
    /** Puts the table on the clipboard as the CSV download holds it. */
    copyCsv: () => Promise<void>;
    /** Every record of a cell of several, as at full size; three at most otherwise. */
    allRecords?: boolean;
}

interface Run {
    /** The index of the run's first row among the table's rows. */
    start: number;
    rows: FigureCell[][];
}

/** The rows in runs of one record of the group column, in their order. */
const runsOf = (rows: FigureCell[][]): Run[] =>
    rows.reduce<Run[]>((runs, row, index) => {
        const last = runs.at(-1);
        if (last && (last.rows[0][0] as FigureRecordCell).id === (row[0] as FigureRecordCell).id) {
            last.rows.push(row);
        } else {
            runs.push({ start: index, rows: [row] });
        }
        return runs;
    }, []);

/**
 * A table Figure's body: its box, the rows in the Figure's columns, and "Copy table as CSV" under the box. Under a
 * 32rem Figure (the panel, the phone) a row's second record chip stacks under its first, so the values stay in view;
 * from 32rem its own column yields width to the others.
 * A group column is no column: each run of its record heads its rows once, as a row of its own.
 * An inline table's footer rows close the table under a divider, the first cell their label: always shown, whatever
 * "Show more" holds back.
 */
export function FigureTable({ rows, limit, copyCsv, allRecords }: Props) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const { columns } = rows;
    const grouped = columns[0]?.group === true;
    const shownAt = columns.flatMap((_, index) => (grouped && index === 0 ? [] : [index]));
    const [lead, stacked] = shownAt.filter((index) => columns[index].type === 'record');
    const shown = limit === undefined || open ? rows.rows.length : limit;
    const more = limit === undefined ? 0 : rows.rows.length - limit;
    const runs = grouped ? runsOf(rows.rows) : [{ start: 0, rows: rows.rows }];
    const cellClassName = (index: number) =>
        cn(
            CELL_CLASS_NAME,
            ONE_LINE.includes(columns[index].type) && 'whitespace-nowrap',
            NUMERIC.includes(columns[index].type) && 'text-end tabular-nums',
            index === stacked && '@max-lg:hidden'
        );
    const decimals = columns.map((column, index) =>
        columnDecimals(
            column,
            [...rows.rows, ...(rows.footer ?? [])].map((row) => row[index] ?? null)
        )
    );

    return (
        <>
            <div className={TABLE_BOX_CLASS_NAME}>
                <table className="text-meta w-full border-collapse">
                    <thead>
                        <tr className="bg-surface-subtle text-caption text-foreground-tertiary">
                            {shownAt.map((index) => (
                                <th
                                    key={columns[index].field}
                                    scope="col"
                                    className={cn(
                                        CELL_CLASS_NAME,
                                        'font-medium',
                                        NUMERIC.includes(columns[index].type) ? 'text-end' : 'text-start',
                                        ONE_LINE.includes(columns[index].type) && 'whitespace-nowrap',
                                        index === stacked && '@max-lg:hidden'
                                    )}
                                >
                                    {columnLabel(columns[index])}
                                    {index === lead && stacked !== undefined ? (
                                        <span className="@lg:hidden"> · {columnLabel(columns[stacked])}</span>
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {runs.map((run) => (
                        // The rows never reorder in place: a fresh read is a fresh table.
                        <tbody key={run.start}>
                            {grouped ? (
                                <tr
                                    hidden={run.start >= shown}
                                    className="border-divider-soft bg-surface-subtle border-t"
                                >
                                    <th
                                        scope="rowgroup"
                                        colSpan={shownAt.length}
                                        className={cn(CELL_CLASS_NAME, 'max-w-0 text-start font-normal')}
                                    >
                                        <EllipsisTooltip>
                                            <FigureCellValue
                                                cell={run.rows[0][0] ?? null}
                                                column={columns[0]}
                                                chipClassName={CUT_CHIP_CLASS_NAME}
                                                hideChipTitle
                                            />
                                        </EllipsisTooltip>
                                    </th>
                                </tr>
                            ) : null}
                            {run.rows.map((row, offset) => (
                                <tr
                                    key={run.start + offset}
                                    hidden={run.start + offset >= shown}
                                    className="border-divider-soft hover:bg-row-stripe border-t"
                                >
                                    {shownAt.map((index) => (
                                        <td key={columns[index].field} className={cellClassName(index)}>
                                            {index === lead && stacked !== undefined ? (
                                                <div className="flex flex-col items-start gap-1">
                                                    <CellValue
                                                        cell={row[index] ?? null}
                                                        column={columns[index]}
                                                        allRecords={allRecords}
                                                    />
                                                    <span className="flex max-w-full @lg:hidden">
                                                        <CellValue
                                                            cell={row[stacked] ?? null}
                                                            column={columns[stacked]}
                                                            allRecords={allRecords}
                                                        />
                                                    </span>
                                                </div>
                                            ) : index === stacked ? (
                                                <span className={YIELDING_CLASS_NAME}>
                                                    <CellValue
                                                        cell={row[index] ?? null}
                                                        column={columns[index]}
                                                        allRecords={allRecords}
                                                        chipClassName={CUT_CHIP_CLASS_NAME}
                                                    />
                                                </span>
                                            ) : (
                                                <CellValue
                                                    cell={row[index] ?? null}
                                                    column={columns[index]}
                                                    allRecords={allRecords}
                                                    decimals={decimals[index]}
                                                />
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ))}
                    {rows.footer?.length ? (
                        <tfoot>
                            {rows.footer.map((row, offset) => (
                                <tr
                                    key={offset}
                                    // A 2px rule over the first, as the reporting pivot table sets its totals apart.
                                    className={
                                        offset === 0 ? 'border-border border-t-2' : 'border-divider-soft border-t'
                                    }
                                >
                                    {shownAt.map((index, position) => {
                                        const cell = row[index] ?? null;
                                        return position === 0 ? (
                                            <th
                                                key={columns[index].field}
                                                scope="row"
                                                className={cn(CELL_CLASS_NAME, 'text-start align-top font-medium')}
                                            >
                                                {cellText(cell)}
                                            </th>
                                        ) : (
                                            // At the top, as a range's first line: a row of ranges is taller.
                                            <td
                                                key={columns[index].field}
                                                className={cn(cellClassName(index), 'align-top')}
                                            >
                                                {isRangeCell(cell) ? (
                                                    <RangeValue
                                                        cell={cell}
                                                        column={columns[index]}
                                                        decimals={decimals[index]}
                                                    />
                                                ) : (
                                                    <CellValue
                                                        cell={cell}
                                                        column={columns[index]}
                                                        decimals={decimals[index]}
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tfoot>
                    ) : null}
                </table>
            </div>
            {more > 0 ? (
                <div className="px-4 pt-1 pb-1.5">
                    <ShowMoreButton open={open} more={more} onToggle={() => setOpen(!open)} />
                </div>
            ) : null}
            <div className="flex items-center gap-1 px-2 pb-1.5">
                <FigureCopyButton label={intl.formatMessage({ id: 'ai-chat.figure.copy-table' })} copy={copyCsv} />
            </div>
        </>
    );
}
